import type { ColumnFormat } from "./columnFormat";
import { formatColumnValue } from "./columnFormat";
import type { NormalizedTablePayload, TableColumnDef } from "./tablePayload";
import { getTableCellDisplay, getTableCellRaw } from "./tablePayload";

export type TableSortDirection = "asc" | "desc";

export type TableSortState = {
  field: string;
  direction: TableSortDirection;
} | null;

function parseSortableNumber(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "boolean") return value ? 1 : 0;
  const text = String(value).trim();
  if (!text) return null;
  const normalized = text.replace(/\s/g, "").replace(/,/g, ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseSortableDateMs(value: unknown): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }
  const text = String(value).trim();
  if (!text) return null;
  if (/^\d+$/.test(text)) {
    const num = Number(text);
    return num > 1e12 ? num : num * 1000;
  }
  const ms = Date.parse(text);
  return Number.isNaN(ms) ? null : ms;
}

function compareRawValues(a: unknown, b: unknown, format?: ColumnFormat): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (format?.type === "date") {
    const da = parseSortableDateMs(a);
    const db = parseSortableDateMs(b);
    if (da != null && db != null) return da - db;
  }

  if (format?.type === "number" || format?.type === "currency") {
    const na = parseSortableNumber(a);
    const nb = parseSortableNumber(b);
    if (na != null && nb != null) return na - nb;
  }

  const na = parseSortableNumber(a);
  const nb = parseSortableNumber(b);
  if (na != null && nb != null) return na - nb;

  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

export function sortTableRows(
  rows: Record<string, unknown>[],
  columns: TableColumnDef[],
  sort: TableSortState
): Record<string, unknown>[] {
  if (!sort) return rows;
  const column = columns.find((c) => (c.field || c.header) === sort.field);
  if (!column) return rows;

  const field = column.field || column.header;
  const sorted = [...rows];
  sorted.sort((left, right) => {
    const cmp = compareRawValues(
      getTableCellRaw(left, field, column.header),
      getTableCellRaw(right, field, column.header),
      column.format
    );
    return sort.direction === "asc" ? cmp : -cmp;
  });
  return sorted;
}

export function filterTableRows(
  rows: Record<string, unknown>[],
  columns: TableColumnDef[],
  query: string
): Record<string, unknown>[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return rows;

  return rows.filter((row) =>
    columns.some((col) => {
      const field = col.field || col.header;
      const raw = getTableCellRaw(row, field, col.header);
      const display = getTableCellDisplay(row, field, col.header, col.format).toLowerCase();
      const rawText = raw == null ? "" : String(raw).toLowerCase();
      return display.includes(needle) || rawText.includes(needle);
    })
  );
}

export function applyTableViewState(
  payload: NormalizedTablePayload,
  filter: string,
  sort: TableSortState
): NormalizedTablePayload {
  const filtered = filterTableRows(payload.rows, payload.columns, filter);
  const rows = sortTableRows(filtered, payload.columns, sort);
  return { ...payload, rows };
}

export function nextTableSortState(current: TableSortState, field: string): TableSortState {
  if (!current || current.field !== field) {
    return { field, direction: "asc" };
  }
  if (current.direction === "asc") {
    return { field, direction: "desc" };
  }
  return null;
}

/** Display value for sorting/filtering consistency tests. */
export function formatTableCellForView(
  row: Record<string, unknown>,
  column: TableColumnDef
): string {
  const field = column.field || column.header;
  return getTableCellDisplay(row, field, column.header, column.format);
}

export { formatColumnValue };
